import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';
import { getDayOfWeek } from '../date';
import { datetimePartDateTrunc } from '../sql';
import { XYChartAxis } from './xy-chart-axis';

/**
 * A helper class for formatting axis labels in ECharts.
 */
export class EChartsAxisHelper {
    /**
     * Creates a formatter function for axis labels.
     *
     * @param formatter - A function that formats the axis label value.
     * @param context - Optional context that can be used within the formatter function.
     * @returns A function that takes a value and returns a formatted string using the provided formatter and context.
     */
    static createAxisLabelFormatter(
        formatter: (value: any, context?: IQueryContext) => string,
        context?: IQueryContext
    ) {
        return (value: any) => formatter(value, context);
    }

    /**
     * Formats a date value for use as an axis label.
     *
     * @param xLabelValue - The date value to format, represented as the number of milliseconds since the Unix Epoch.
     * @param context - An optional query context that can contain settings for date aggregation.
     *
     * @returns A string representing the formatted date.
     */
    static axisLabelDateFormatter(
        xLabelValue: number,
        context?: IQueryContext
    ) {
        // new UTC date
        const dateLocal = new Date(xLabelValue);
        const dateUTC = new Date(
            Date.UTC(
                dateLocal.getFullYear(),
                dateLocal.getMonth(),
                dateLocal.getDate()
            )
        );

        // format dateUTC to string YYYY-MM-DD
        const prefix = (() => {
            if (!context) return '';
            if (!context.settings.dateAggregation) return '';
            if (context.settings.dateAggregation === 'total') return '';
            return `${
                context.settings.dateAggregation?.toUpperCase() || 'DAY'
            }: `;
        })();
        return `${prefix}${EChartsAxisHelper.dateToLabelString(
            dateUTC,
            context?.settings.dateAggregation || 'day'
        )}`;
        // return `${prefix}${dateUTC.toISOString().split('T')[0]}`;
    }

    /**
     * Formats a date value for use as an axis label.
     *
     * @param xLabelValue - The date value to format, represented as the number of milliseconds since the Unix Epoch.
     * @param context - An optional query context that can contain settings for date aggregation.
     *
     * @returns A string representing the formatted date.
     */
    static axisLabelDatetimeTooltipFormatter(
        xLabelValue: Date,
        context?: IQueryContext
    ) {
        // xLabelValue is date
        if (
            typeof xLabelValue !== 'object' ||
            xLabelValue.getTime === undefined
        ) {
            // try to convert to date
            xLabelValue = new Date(xLabelValue);
            if (isNaN(xLabelValue.getTime())) {
                throw new Error(
                    `axisLabelDatetimeFormatter: xLabelValue is not a date or cannot be converted to date: ${xLabelValue}`
                );
            }
        }
        // throw error if context.settings.datetimeAggregation is not defined
        if (!context?.settings.datetimeAggregation) {
            throw new Error(
                'axisLabelDatetimeFormatter: context.settings.datetimeAggregation is not defined'
            );
        }
        const aggregation = context.settings.datetimeAggregation;
        const label = EChartsAxisHelper.dateToLabelString(
            xLabelValue,
            aggregation
        );

        const prefix = context ? `${aggregation?.toUpperCase()}: ` : '';
        return `${prefix}${label}`;
    }

    private static dateToLabelString(
        date: Date,
        aggregation: datetimePartDateTrunc
    ): string {
        switch (aggregation) {
            case 'second':
                return date.toISOString().slice(0, 19); // YYYY-MM-DDTHH:MM:SS
            case 'minute':
                return date.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
            case 'hour':
                return date.toISOString().slice(0, 13); // YYYY-MM-DDTHH
            case 'day':
                return `${date.toISOString().slice(0, 10)} (${getDayOfWeek(
                    date,
                    'short'
                )})`; // YYYY-MM-DD (Weekday)
            case 'week':
                return date.toISOString().slice(0, 10); // YYYY-MM-DD
            case 'month':
                return `${date.toISOString().slice(0, 7)}`; // YYYY-MM
            case 'quarter': {
                const month = date.getUTCMonth();
                const quarter = Math.floor(month / 3) + 1;
                return `${date.toISOString().slice(0, 7)} (Q${quarter})`; // YYYY-MM Qn
            }
            case 'year':
                return date.getUTCFullYear().toString(); // YYYY
            case 'total':
                return 'Total';
            default:
                return date.toISOString().slice(0, 13); // YYYY-MM-DDTHH
        }
    }

    /**
     * Formats a number value for use as an axis label.
     *
     * @param value - The number value to format.
     *
     * @returns A string representing the formatted number. If the number is greater than or equal to 1000000, it is represented in millions ('M') with one decimal place if not divisible by 1000000. If the number is greater than or equal to 1000 but less than 1000000, it is represented in thousands ('k') with one decimal place if not divisible by 1000. Otherwise, the original number is returned.
     */
    static axisNumberLabelFormatter(value: number): string {
        // get abs of value
        const absValue = Math.abs(value);
        if (absValue >= 1000000) {
            if (absValue % 1000000 === 0) {
                // If divisible by 1,000,000, represent in millions ('M')
                return (value / 1000000).toFixed(0) + 'M';
            } else {
                // If not divisible by 1,000,000, represent in millions ('M') with one decimal place
                return (value / 1000000).toFixed(1) + 'M';
            }
        } else if (absValue >= 1000) {
            if (absValue % 1000 === 0) {
                // If divisible by 1,000, represent in thousands ('k')
                return (value / 1000).toFixed(0) + 'k';
            } else {
                // If not divisible by 1,000, represent in thousands ('k') with one decimal place
                return (value / 1000).toFixed(1) + 'k';
            }
        } else {
            // Otherwise, return the original number as a string
            return value.toString();
        }
    }

    /**
     * Formats a fraction as a percentage.
     *
     * @param value - The fraction to format.
     * @param precision - The number of decimal places to include in the percentage. If not provided, 0 is used.
     *
     * @returns A string representing the fraction as a percentage. For example, 0.01 is returned as '1%'.
     */
    static axisPercentLabelFormatter(value: number, precision: number): string {
        if (precision === undefined) {
            precision = 0;
        }
        return (value * 100).toFixed(precision) + '%';
    }

    static getAxisPercentLabelFormatter(precision: number) {
        return (value: number) =>
            EChartsAxisHelper.axisPercentLabelFormatter(value, precision);
    }
}
